import { useEffect, useState } from "react";
import styles from "./ServiceOffer.module.css";
import ServiceOfferingsLabels from "./ServiceOfferingsLabels";
import { useNavigate, useSearchParams } from "react-router-dom";
import OpenAcctIcon from "../../assets/icons/OpenAcctIcon";
import ExistingCustomerIcon from "../../assets/icons/ExistingCustomerIcon";
import UpgradeAcctIcon from "../../assets/icons/UpgradeAcctIcon";
import PersonalIcon from "../../assets/icons/PersonalIcon";
import BusinessIcon from "../../assets/icons/BusinessIcon";
import { errorToast } from "../../utils/makeToast";
import { pushFormStarted } from "../../utils/dataLayerEvents";
import CustomModal from "../Modal/CustomModal";
import SelectAccountUpgradeType from "./SelectAccountUpgradeType";
import AccountFeatures from "../UpgradeAccountDialog/AccountFeatures";
import TermsAndConditions from "../TermsAndConditions";
import { useAppSelector, useAppDispatch } from "../../redux/hooks";
import { completeStepper, saveStepperData } from "../../redux/stepperSlice";
import { TierType, UpgradeAccountRequest } from "../../types/upgradeAccount";
import apiHooks from "../../customHooks/apis";

type AccType = "new" | "existing" | "upgrade" | "";

export default function ServiceOfferings() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [accountType, setAccountType] = useState<AccType>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const currentView = searchParams.get("view") || "";
  const upgradeAccType = searchParams.get("type") || "";
  const removeSearchParams = (param: string) => {
    searchParams.delete(param);
    setSearchParams(searchParams);
  };
  const {} = apiHooks.useGetReligions();
  const handleClickProceed = () => {
    searchParams.set("view", "features");
    setSearchParams(searchParams);
  };

  const modalTitleMap = {
    features: {
      modalTitle:
        upgradeAccType === "tier2"
          ? "Tier 2 Account"
          : "Classic Savings Account",
      onClose: () => removeSearchParams("view"),
    },

    "terms-and-conditions": {
      modalTitle: "Terms and Conditions",
      onClose: handleClickProceed,
    },
    "": {
      modalTitle: "Select Account Type",
      onClose: () => {
        setAccountType("");
        removeSearchParams("type");
        dispatch(
          saveStepperData<Partial<UpgradeAccountRequest>>({
            tierType: 0 as TierType,
          }),
        );
      },
    },
  };
  const { termsAndConditions } = useAppSelector((state) => state.stepper.data);

  const handleClickAccType = (accType: AccType) => {
    if (!accType) return setAccountType("");
    if (accType === "existing") return errorToast("Coming Soon");
    setAccountType(accType);
  };

  const routeToPersonal = () => {
    pushFormStarted("personal");
    if (accountType === "new") {
      navigate("/personal-account");
    }
    if (accountType === "existing") {
      navigate("/ex-personal-account");
    }
  };
  const routeToBusiness = () => {
    pushFormStarted("corporate");
    if (accountType === "new") {
      navigate("/open-business-info");
    }
    if (accountType === "existing") {
      navigate("/business-account");
    }
  };

  const handleClickCheck = (state: boolean) =>
    dispatch(
      saveStepperData<Partial<UpgradeAccountRequest>>({
        termsAndConditions: state,
      }),
    );

  useEffect(() => {
    dispatch(completeStepper());
  }, [dispatch]);

  return (
    <>
      <div className={styles.main}>
        <h1>We are excited to meet you</h1>
        <p>Let's get started...</p>
        <div className={styles.customerFlex}>
          <ServiceOfferingsLabels
            headerText="Open Account"
            icon={<OpenAcctIcon />}
            subText="Create a new account"
            onClick={() => handleClickAccType("new")}
            selected={accountType === "new"}
          />
          <ServiceOfferingsLabels
            headerText="Existing customer"
            icon={<ExistingCustomerIcon />}
            subText="Open an additional account"
            onClick={() => handleClickAccType("existing")}
            selected={accountType === "existing"}
          />
          <ServiceOfferingsLabels
            headerText="Upgrade Account"
            icon={<UpgradeAcctIcon />}
            subText="Upgrade your swift Account"
            onClick={() => handleClickAccType("upgrade")}
            selected={accountType === "upgrade"}
          />
        </div>

        {accountType && (
          <div>
            <p className={styles.text}>
              What type of account do you want to open?
            </p>
            <div className={styles.container}>
              <ServiceOfferingsLabels
                headerText="Personal"
                icon={<PersonalIcon />}
                dropDown
                onClick={routeToPersonal}
              />
              <ServiceOfferingsLabels
                headerText="Business"
                icon={<BusinessIcon />}
                dropDown
                onClick={routeToBusiness}
              />
            </div>
          </div>
        )}
      </div>
      <CustomModal
        modalTitle={modalTitleMap?.[currentView]?.modalTitle}
        show={accountType === "upgrade"}
        onClickClose={modalTitleMap?.[currentView]?.onClose}
      >
        <SelectAccountUpgradeType onClickCancel={modalTitleMap[""].onClose} />
        <AccountFeatures
          checked={termsAndConditions}
          onClickCheck={handleClickCheck}
        />
        {currentView === "terms-and-conditions" && (
          <TermsAndConditions
            onClickCheck={handleClickCheck}
            onClickProceed={handleClickProceed}
          />
        )}
      </CustomModal>
    </>
  );
}
